import * as THREE from 'three';
import { createSword, preloadSwordModel } from './mesh';
import {
  DOMINANT_HAND,
  GUARD_GHOST_COLOR_HEX,
  GUARD_GHOST_MATCH_COLOR_HEX,
  GUARD_GHOST_OPACITY,
  GUARD_PROTOTYPES,
  GUARD_PROTOTYPE_USER_HEIGHT_M,
  type DominantHand,
  type Guard,
} from '../config/constants';

const UP = new THREE.Vector3(0, 1, 0);

/**
 * The spada fantasma (VG3): a translucent sword posed at a guard's *prototype* in the user's
 * live body frame — the same frame the recognizer uses, so aligning to it ≡ being recognized.
 * Tints green when the held guard matches the shown one. Hidden until `show`n.
 *
 * The prototype is authored for a right-hander; for a left-hander the lateral (sword-side)
 * component is mirrored, exactly as the recognizer mirrors lato via the dominant-relative right.
 */
export class GhostSword {
  readonly group: THREE.Group;
  private readonly material: THREE.MeshBasicMaterial;
  private disposed = false;
  private modelUpgraded = false;

  // Scratch — transient within `show`, never retained.
  private readonly bladeWorld = new THREE.Vector3();
  private readonly edgeWorld = new THREE.Vector3();
  private readonly xAxis = new THREE.Vector3();
  private readonly yAxis = new THREE.Vector3();
  private readonly zAxis = new THREE.Vector3();
  private readonly basis = new THREE.Matrix4();

  constructor() {
    // Start with primitive placeholders (synchronous); swap in a translucent clone of the
    // real GLB once it loads. A single shared material — which we own — lets us tint the ghost.
    this.group = createSword();
    this.material = new THREE.MeshBasicMaterial({
      color: GUARD_GHOST_COLOR_HEX,
      transparent: true,
      opacity: GUARD_GHOST_OPACITY,
      depthWrite: false,
    });
    this.applyGhostMaterial(this.group, true);
    this.group.visible = false;
    void this.upgradeToModel();
  }

  /**
   * Replace the primitive placeholders with a translucent clone of the real sidesword GLB,
   * so the spada fantasma reads as a ghostly copy of the held sword rather than a rough blob.
   * On load failure the primitives stay — same fallback as the held sword's `upgradeSwordModel`.
   */
  private async upgradeToModel(): Promise<void> {
    let model: THREE.Group;
    try {
      model = await preloadSwordModel();
    } catch (err) {
      console.warn('[ghostSword] model load failed, keeping primitive ghost', err);
      return;
    }
    if (this.disposed) return; // disposed while the GLB was loading

    for (const child of [...this.group.children]) {
      this.group.remove(child);
      if (child instanceof THREE.Mesh) (child.geometry as THREE.BufferGeometry).dispose();
    }
    // Clone so the ghost owns its own scene graph: the GLB is a shared singleton living in the
    // player's hand. The clone shares geometry (cheap) — we override the materials anyway.
    const clone = model.clone();
    this.applyGhostMaterial(clone, false);
    this.group.add(clone);
    this.modelUpgraded = true;
  }

  /**
   * Assign the shared translucent material to every mesh under `root`.
   * `disposeOld` only when the replaced materials are owned here (the primitives) — never for
   * the GLB clone, whose materials are shared with the singleton model in the player's hand.
   */
  private applyGhostMaterial(root: THREE.Object3D, disposeOld: boolean): void {
    root.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        if (disposeOld) (o.material as THREE.Material).dispose();
        o.material = this.material;
      }
    });
  }

  /** Pose the ghost at `guard`'s prototype in the body frame `(forward, right)` rooted at the head. */
  show(
    guard: Guard,
    headPos: THREE.Vector3,
    forward: THREE.Vector3,
    right: THREE.Vector3,
    dominantHand: DominantHand,
    userHeightM: number,
  ): void {
    const proto = GUARD_PROTOTYPES[guard];
    const mirror = dominantHand === DOMINANT_HAND.right ? 1 : -1;
    // Prototype offsets are authored at GUARD_PROTOTYPE_USER_HEIGHT_M; scale them by the user's
    // height so the pommel sits at the same fraction of body height for everyone (like the enemy).
    const scale = userHeightM / GUARD_PROTOTYPE_USER_HEIGHT_M;

    this.toWorld(proto.hand, forward, right, mirror, this.group.position)
      .multiplyScalar(scale)
      .add(headPos);

    this.toWorld(proto.blade, forward, right, mirror, this.bladeWorld);
    this.toWorld(proto.edge, forward, right, mirror, this.edgeWorld);
    // Sword local frame: blade = −Z, edge = +X. Orthonormalize (blade exact, edge Gram-Schmidt).
    this.zAxis.copy(this.bladeWorld).normalize().negate();
    this.xAxis
      .copy(this.edgeWorld)
      .addScaledVector(this.zAxis, -this.edgeWorld.dot(this.zAxis))
      .normalize();
    this.yAxis.crossVectors(this.zAxis, this.xAxis);
    this.group.quaternion.setFromRotationMatrix(
      this.basis.makeBasis(this.xAxis, this.yAxis, this.zAxis),
    );
    this.group.visible = true;
  }

  hide(): void {
    this.group.visible = false;
  }

  /** Green when the held guard matches the shown one, neutral ivory otherwise. */
  setMatched(matched: boolean): void {
    this.material.color.setHex(matched ? GUARD_GHOST_MATCH_COLOR_HEX : GUARD_GHOST_COLOR_HEX);
  }

  dispose(): void {
    this.disposed = true;
    this.group.parent?.remove(this.group);
    // The GLB clone shares its geometry with the shared singleton model — disposing it would
    // break the held sword. Only the primitive placeholders own their geometry.
    if (!this.modelUpgraded) {
      this.group.traverse((o) => {
        if (o instanceof THREE.Mesh) (o.geometry as THREE.BufferGeometry).dispose();
      });
    }
    this.material.dispose();
  }

  /** Map a canonical body-frame vector (x = sword side, y = up, z<0 = forward) into world. */
  private toWorld(
    v: THREE.Vector3,
    forward: THREE.Vector3,
    right: THREE.Vector3,
    mirror: number,
    out: THREE.Vector3,
  ): THREE.Vector3 {
    return out
      .set(0, 0, 0)
      .addScaledVector(right, v.x * mirror)
      .addScaledVector(UP, v.y)
      .addScaledVector(forward, -v.z);
  }
}
